import { createContext, useEffect, useMemo, useContext, PropsWithChildren } from 'react';

import { FeatureDefinition, GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { AnalyticsContext } from '@arrived/analytics';
import { CONFIG } from '@arrived/config';

export type FeatureList = Record<string, FeatureDefinition>;

interface FeatureFlagContextType {
  isLoading: boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>({ isLoading: true });

export const FeatureFlagContextProvider = ({ children }: PropsWithChildren) => {
  const { isLoading: isRudderLoading, rudder, getAnonymousId } = useContext(AnalyticsContext);
  const growthbook = useMemo(() => new GrowthBook(), []);

  const featuresState = useQuery(
    ['growthbook', 'feature-flags'],
    async () => axios.get<{ features: FeatureList }>(CONFIG.growthbookApiRoot).then((response) => response.data),
    {
      onSuccess: (data) => growthbook.setFeatures(data.features),
      staleTime: Infinity,
    }
  );

  const value = useMemo(() => ({ isLoading: featuresState.isLoading }), [featuresState.isLoading]);

  useEffect(() => {
    if (!isRudderLoading && rudder && getAnonymousId) {
      // Attribute overrides get merged with attributes in Growthbook, if we just setAttributes then the attributes set
      // elsewhere will be overridden.
      growthbook.setAttributeOverrides({ rudderStackId: getAnonymousId() });
    }
  }, [isRudderLoading, growthbook, rudder]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
    </GrowthBookProvider>
  );
};
